// © Microsoft Corporation. All rights reserved.

import { dispatchAction, getActionPayload } from "./ActionDispatchHelper";

import { IActionPayload, IDataConnector, ConnectorConstants } from "@msnews/experiences-redux-contracts";
import { Observer } from "./ActionObserver";
import { PerfTimeline } from "@msnews/diagnostics";

/**
 * Class that wraps the sending of a action to a connector (i.e. dispatch) with an explicit method, so that in the consuming code
 * it is obvious that the action is being "sent"
 * @class
 */
export abstract class ActionSenderBase<TActionSignature extends Function> {
    /**
     * Property which is a function that will send an action to the connector.
     */
    public get send(): TActionSignature {
        return (this.dispatchAction());
    }

    /**
     * Creates an instance of the `ActionSender` class.
     * @constructor
     * @param {string} type The action type.
     * @param {Observer[]} observers Array of registered action observers
     * @param {IDataConnector<any, any>} connector Optional connector to send the action to
     */
    constructor(
        protected type: string,
        private observers: Observer[],
        protected connector?: IDataConnector<any, any>) { }

    /**
     * Method to get dispatcher and payload
     * @param args - the arguments being dispatched
     * @returns dispatch result metadata
     */
    protected abstract getDispatchResult(args: any[]): { payload: IActionPayload, result: any };

    /**
     * Gets the perf timeline object if applicable
     */
    protected abstract tryGetPerfTimeline(): PerfTimeline;

    /**
     * Method that returns an action sender function to send the action.
     * @method
     * @return {TActionSignature} return value is a function to send to action.
     */
    private dispatchAction(): TActionSignature {
        return <TActionSignature><unknown> function (...args: unknown[]): unknown {
            const perfTimeline = this.tryGetPerfTimeline();
            const actionEndMeasure = perfTimeline && perfTimeline.startMeasure("action", this.type).endMeasure;

            const { payload, result } = this.getDispatchResult(args);

            // Call any observers
            this.observers.forEach(o => {
                try {
                    if (!o.filterConnector
                        || this.connector === o.filterConnector) {

                        // Call the observer
                        o.observer(payload);
                    }
                } catch (error) {
                    console.log(`Error calling observer: ${JSON.stringify(o.observer)}, with error: ${error}`);
                }

                // If there is a dispatch connector, dispatch an action to it, forcing it to re-render
                if (o.dispatchConnector) {
                    const renderActionPayload = getActionPayload(o.dispatchConnector, ConnectorConstants.renderAction, [], undefined);
                    dispatchAction(o.dispatchConnector, renderActionPayload);
                }
            });

            if (actionEndMeasure) {
                actionEndMeasure();
            }

            // Return the dispatch result
            return result;
        };
    }

}